import React, { FC, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import { components } from 'react-select';
import classNames from 'classnames';

import type { LiveStreamEntries } from '../../types';
import type { ReduxState, ThunkDispatchType } from 'app/redux/types';

import { Icon, IconSize } from 'app/design-system/components/Icon';
import type { IconName } from 'app/design-system/types/Icon';
import { tFunction, useTranslation } from 'app/lang';
import { SportId } from 'app/lib/betting/constants';
import { getSportKey } from 'app/lib/betting/sport-utils';
import { replacingHyphenOnDash } from 'app/lib/replaceDashSymbol';
import { type OpenLoginDialog, openLoginDialog } from 'app/modules/Signing/actions';
import { selectIsLoggedIn } from 'app/redux/selectors/auth';
import Dropdown, { type ChangeHandler, Control, Option } from 'app/widgets/Dropdown/Single';

import type { SelectStreamWithAutoplay } from '../../actions';
import { selectStreamWithAutoplay } from '../../actions';
import { selectLiveStreamEntries, selectStreamId } from '../../selectors';

import dictionary from './MatchesDropdown.dictionary.json';

import styles from './MatchesDropdown.module.scss';

// ---

type DropdownOption = {
  label: string;
  value: number;
  sportId: SportId;
  matchId:number;
  showSportTile: boolean;
  topMatch: boolean;
};

type StoreProps = {
  entries: LiveStreamEntries;
  streamId: number | null;
  isSignedIn: boolean;
};

type DispatchProp = {
  dispatch: ThunkDispatchType<SelectStreamWithAutoplay | OpenLoginDialog>;
};

const MatchesDropdown: FC<DispatchProp & StoreProps> = ({
  entries,
  streamId,
  dispatch,
  isSignedIn,
}) => {
  const { t } = useTranslation();

  const options = useMemo(() => {
    const ids = new Set();

    return entries.reduce((acc: DropdownOption[], entry) => {
      if (!entry.matchId || !entry.sportId) {
        console.error(`MatchesDropdown -> matchId = ${entry.matchId} or sportId = ${entry.sportId} - not found`);
        return acc;
      }

      const hasSportId = ids.has(entry.sportId);

      if (!hasSportId) {
        ids.add(entry.sportId);
      }

      acc.push({
        label: replacingHyphenOnDash(entry.matchName),
        value: entry.matchId,
        sportId: entry.sportId,
        matchId: entry.matchId,
        showSportTile: !hasSportId,
        topMatch: !!(entry.isLeading),
      });

      return acc;
    }, [])
      .sort((a, b) => a.sportId - b.sportId);
  }, [entries]);

  const selectOptionHandler: ChangeHandler<DropdownOption> = useCallback((option) => {
    if (!isSignedIn) {
      dispatch(openLoginDialog());
      return;
    }

    if (!option || !('value' in option)) {
      console.error('MatchesDropdown -> selectOptionHandler -> `option` not found', option);
      return;
    }

    dispatch(selectStreamWithAutoplay(option.value));
  }, [isSignedIn]);

  const value = options.find((option) => option.value === streamId) || null;

  return (
    <Dropdown<DropdownOption>
      dynamicOptions
      menuPortalTarget={process.env.SERVER ? null : document.body}
      placeholder={t(dictionary.placeholder)}
      isSearchable={false}
      options={options}
      value={value}
      className={styles.dropdown}
      indicatorSize={IconSize.MEDIUM}
      onChange={selectOptionHandler}
      components={{
        SingleValue: (props) => {
          return (
            <components.SingleValue {...props}>
              <Icon icon={getSportIconName(props.data.sportId)}
                    className={styles.sportIconSingleValue}
                    size={16} />
              {props.children}
            </components.SingleValue>
          );
        },
        Option: (props) => {
          const { sportId, matchId, showSportTile, topMatch } = props.data;

          const sportName = getSportName(entries, matchId, t);

          const optionClassNames = classNames({
            [styles.topMatch]: topMatch,
          });

          return (
            <>
              {showSportTile && (
                <div className={styles.sportInfo}>
                  <Icon icon={getSportIconName(sportId)}
                        className={styles.sportIcon}
                        size={16} />
                  <span className={styles.sportType}>
                    {sportName}
                  </span>
                </div>
              )}
              <Option {...props}
                      customInnerProps={{
                        ['data-ga-event']: 'right-broadcastSelect',
                        ['data-ga-sport-match']: matchId,
                      }}>
                <div className={optionClassNames}>
                  {props.children}
                </div>
              </Option>
            </>
          );
        },
        MenuPortal: (props) => (
          <components.MenuPortal {...props}>
            <div className={styles.portal}>
              {props.children}
            </div>
          </components.MenuPortal>
        ),
        Control: (props) => (
          <Control {...props}
                   customInnerProps={{
                     title: t(dictionary.selectTitle),
                     ['data-ga-event']: 'right-broadcastMenu',
                   }}>
            {props.children}
          </Control>
        ),
      }} />
  );
};

function getSportIconName(sportId: number): IconName {
  return `sport-${getSportKey(sportId)}` as IconName;
}

function getSportName(entries:LiveStreamEntries, matchId:number, t:tFunction) {
  return entries.get(matchId)?.sportName || t(dictionary.unknownSport);
}

const mapStateToProps = (state: ReduxState): StoreProps => ({
  entries: selectLiveStreamEntries(state),
  streamId: selectStreamId(state),
  isSignedIn: selectIsLoggedIn(state),
});

export default connect(mapStateToProps)(MatchesDropdown);
